import { PLAN_NODES, PRODUCT_DATA, leafChannelsUnder, yearTarget } from "./data"

export interface Row {
  id: string
  name: string
  // Plan node path under a product name; empty for plan node rows.
  subLabel: string | null
  actual: number
  plan: number
  cogs: number
  units: number | null
  target: number | null
}

// Product rows for a scope: each product's figures summed across the leaf
// channels under it. A product with no plan in any of them is left out.
export function productRows(scopeId: string): Row[] {
  const channels = new Set(leafChannelsUnder(scopeId))
  return PRODUCT_DATA.flatMap((p) => {
    const figures = p.byChannel.filter((c) => channels.has(c.channel))
    if (figures.length === 0) return []
    const node = PLAN_NODES.find((n) => n.id === p.planNodeId)
    return [{
      id: p.id,
      name: p.name,
      subLabel: node ? node.path.join(" › ") : null,
      actual: figures.reduce((s, c) => s + c.actual, 0),
      plan: figures.reduce((s, c) => s + c.plan, 0),
      cogs: figures.reduce((s, c) => s + c.cogs, 0),
      units: figures.reduce((s, c) => s + c.units, 0),
      target: null,
    }]
  })
}

// One row per plan node: its products summed, plus the node's year target
// summed across the same channels.
export function planNodeRows(scopeId: string): Row[] {
  const channels = leafChannelsUnder(scopeId)
  const products = productRows(scopeId)
  return PLAN_NODES.flatMap((n) => {
    const ids = new Set(PRODUCT_DATA.filter((p) => p.planNodeId === n.id).map((p) => p.id))
    const rows = products.filter((r) => ids.has(r.id))
    if (rows.length === 0) return []
    return [{
      id: n.id,
      name: n.path.join(" › "),
      subLabel: null,
      actual: rows.reduce((s, r) => s + r.actual, 0),
      plan: rows.reduce((s, r) => s + r.plan, 0),
      cogs: rows.reduce((s, r) => s + r.cogs, 0),
      units: null,
      target: channels.reduce((s, c) => s + yearTarget(n.id, c), 0),
    }]
  })
}

export function totalRow(rows: Row[], withTarget: boolean): Row {
  return {
    id: "total",
    name: "Total",
    subLabel: null,
    actual: rows.reduce((s, r) => s + r.actual, 0),
    plan: rows.reduce((s, r) => s + r.plan, 0),
    cogs: rows.reduce((s, r) => s + r.cogs, 0),
    units: null,
    target: withTarget ? rows.reduce((s, r) => s + (r.target ?? 0), 0) : null,
  }
}

export function gmPct(row: Row): number | null {
  return row.actual > 0 ? ((row.actual - row.cogs) / row.actual) * 100 : null
}
